import { defineStore } from 'pinia';
import { ref, computed, reactive } from 'vue';
import type { Assignment, PaginationState, AssignmentFilters, AssignmentStatus, CourseType } from '../types/assignment';
import { assignmentApi } from '../services/assignmentApi';

const DEFAULT_PAGE_SIZE = 20;

const defaultPagination = (): PaginationState => ({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalCount: 0,
  totalPages: 0,
  hasMore: false,
});

const defaultFilters = (): AssignmentFilters => ({
  status: [],
  courseType: [],
  dateFrom: null,
  dateTo: null,
});

export const useAssignmentStore = defineStore('assignment', () => {
  // === OVERDUE ASSIGNMENTS STATE (dashboard widget) ===
  const assignments = ref<Assignment[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const hasFetchedOverdue = ref(false);

  // === RECENT ASSIGNMENTS STATE (dashboard) ===
  const recentAssignments = ref<Assignment[]>([]);
  const recentLoading = ref(false);
  const recentHasFetched = ref(false);

  // === HISTORY STATE (paginated, in_progress + completed) ===
  const historyAssignments = ref<Assignment[]>([]);
  const historyLoading = ref(false);
  const historyPagination = reactive<PaginationState>(defaultPagination());
  const historyFilters = reactive<AssignmentFilters>(defaultFilters());

  // === MY ASSIGNMENTS STATE (paginated, all statuses) ===
  const myAssignments = ref<Assignment[]>([]);
  const myAssignmentsLoading = ref(false);
  const myAssignmentsPagination = reactive<PaginationState>(defaultPagination());
  const myAssignmentsFilters = reactive<AssignmentFilters>(defaultFilters());

  // === OVERDUE GETTERS ===
  const assignmentCount = computed(() => assignments.value.length);
  const hasAssignments = computed(() => assignments.value.length > 0);
  const overdueAssignments = computed(() =>
    assignments.value.filter((a) => a.status === 'overdue')
  );
  const overdueCount = computed(() => overdueAssignments.value.length);
  const inProgressAssignments = computed(() =>
    assignments.value.filter((a) => a.status === 'in_progress')
  );
  const completedAssignments = computed(() =>
    assignments.value.filter((a) => a.status === 'completed')
  );

  // === RECENT GETTERS ===
  const recentCount = computed(() => recentAssignments.value.length);
  const hasRecent = computed(() => recentAssignments.value.length > 0);

  // === HISTORY GETTERS ===
  const historyCount = computed(() => historyAssignments.value.length);
  const hasHistory = computed(() => historyAssignments.value.length > 0);
  const hasActiveHistoryFilters = computed(() =>
    historyFilters.status.length > 0 ||
    historyFilters.courseType.length > 0 ||
    historyFilters.dateFrom !== null ||
    historyFilters.dateTo !== null
  );

  // === MY ASSIGNMENTS GETTERS ===
  const myAssignmentsCount = computed(() => myAssignments.value.length);
  const hasMyAssignments = computed(() => myAssignments.value.length > 0);
  const hasActiveMyAssignmentsFilters = computed(() =>
    myAssignmentsFilters.courseType.length > 0 ||
    myAssignmentsFilters.dateFrom !== null ||
    myAssignmentsFilters.dateTo !== null
  );

  // === OVERDUE ACTIONS ===
  async function fetchAssignments() {
    loading.value = true;
    error.value = null;
    try {
      assignments.value = await assignmentApi.getAll();
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch assignments';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchOverdueAssignments(force = false) {
    if (hasFetchedOverdue.value && !force) return;

    loading.value = true;
    error.value = null;
    try {
      assignments.value = await assignmentApi.getOverdue();
      hasFetchedOverdue.value = true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch overdue assignments';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  // === RECENT ACTIONS ===
  async function fetchRecentAssignments(force = false) {
    if (recentHasFetched.value && !force) return;

    recentLoading.value = true;
    error.value = null;
    try {
      const response = await assignmentApi.getRecent();
      recentAssignments.value = response.assignments;
      recentHasFetched.value = true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch recent assignments';
      throw e;
    } finally {
      recentLoading.value = false;
    }
  }

  // === HISTORY ACTIONS ===
  async function fetchHistoryPage(page = 1) {
    historyLoading.value = true;
    error.value = null;
    try {
      const response = await assignmentApi.getHistory({
        page,
        pageSize: historyPagination.pageSize,
      });

      historyAssignments.value = response.assignments;
      historyPagination.page = response.page;
      historyPagination.pageSize = response.pageSize;
      historyPagination.totalCount = response.totalCount;
      historyPagination.totalPages = Math.ceil(response.totalCount / response.pageSize);
      historyPagination.hasMore = response.hasMore;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch assignment history';
      throw e;
    } finally {
      historyLoading.value = false;
    }
  }

  function setHistoryFilters(newFilters: Partial<AssignmentFilters>) {
    if (newFilters.status !== undefined) historyFilters.status = newFilters.status;
    if (newFilters.courseType !== undefined) historyFilters.courseType = newFilters.courseType;
    if (newFilters.dateFrom !== undefined) historyFilters.dateFrom = newFilters.dateFrom;
    if (newFilters.dateTo !== undefined) historyFilters.dateTo = newFilters.dateTo;
    historyPagination.page = 1;
  }

  function setHistoryStatusFilter(status: AssignmentStatus[]) {
    historyFilters.status = status;
    historyPagination.page = 1;
  }

  function setHistoryCourseTypeFilter(courseType: CourseType[]) {
    historyFilters.courseType = courseType;
    historyPagination.page = 1;
  }

  function setHistoryDateFilter(dateFrom: string | null, dateTo: string | null) {
    historyFilters.dateFrom = dateFrom;
    historyFilters.dateTo = dateTo;
    historyPagination.page = 1;
  }

  function resetHistoryFilters() {
    Object.assign(historyFilters, defaultFilters());
    historyPagination.page = 1;
  }

  // === MY ASSIGNMENTS ACTIONS ===
  async function fetchMyAssignmentsPage(page = 1) {
    myAssignmentsLoading.value = true;
    error.value = null;
    try {
      const response = await assignmentApi.getInProgress({
        page,
        pageSize: myAssignmentsPagination.pageSize,
      });

      myAssignments.value = response.assignments;
      myAssignmentsPagination.page = response.page;
      myAssignmentsPagination.pageSize = response.pageSize;
      myAssignmentsPagination.totalCount = response.totalCount;
      myAssignmentsPagination.totalPages = Math.ceil(response.totalCount / response.pageSize);
      myAssignmentsPagination.hasMore = response.hasMore;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch my assignments';
      throw e;
    } finally {
      myAssignmentsLoading.value = false;
    }
  }

  function setMyAssignmentsFilters(newFilters: Partial<AssignmentFilters>) {
    if (newFilters.courseType !== undefined) myAssignmentsFilters.courseType = newFilters.courseType;
    if (newFilters.dateFrom !== undefined) myAssignmentsFilters.dateFrom = newFilters.dateFrom;
    if (newFilters.dateTo !== undefined) myAssignmentsFilters.dateTo = newFilters.dateTo;
    myAssignmentsPagination.page = 1;
  }

  function resetMyAssignmentsFilters() {
    Object.assign(myAssignmentsFilters, defaultFilters());
    myAssignmentsPagination.page = 1;
  }

  // === SHARED ACTIONS ===
  function clearError() {
    error.value = null;
  }

  return {
    // Overdue state
    assignments,
    loading,
    error,
    hasFetchedOverdue,
    // Overdue getters
    assignmentCount,
    hasAssignments,
    overdueAssignments,
    overdueCount,
    inProgressAssignments,
    completedAssignments,
    // Overdue actions
    fetchAssignments,
    fetchOverdueAssignments,

    // Recent state
    recentAssignments,
    recentLoading,
    recentHasFetched,
    // Recent getters
    recentCount,
    hasRecent,
    // Recent actions
    fetchRecentAssignments,

    // History state
    historyAssignments,
    historyLoading,
    historyPagination,
    historyFilters,
    // History getters
    historyCount,
    hasHistory,
    hasActiveHistoryFilters,
    // History actions
    fetchHistoryPage,
    setHistoryFilters,
    setHistoryStatusFilter,
    setHistoryCourseTypeFilter,
    setHistoryDateFilter,
    resetHistoryFilters,

    // My Assignments state
    myAssignments,
    myAssignmentsLoading,
    myAssignmentsPagination,
    myAssignmentsFilters,
    // My Assignments getters
    myAssignmentsCount,
    hasMyAssignments,
    hasActiveMyAssignmentsFilters,
    // My Assignments actions
    fetchMyAssignmentsPage,
    setMyAssignmentsFilters,
    resetMyAssignmentsFilters,

    // Shared actions
    clearError,
  };
});
